(() => {
  const clamp = (number, min = 0, max = 1) => Math.min(max, Math.max(min, number));
  const initialise = (root) => {
    if (!root || root.dataset.ihBrainReady === 'true') return;
    root.dataset.ihBrainReady = 'true';
    const steps = Array.from(root.querySelectorAll('[data-ih-brain-step]'));
    const counter = root.querySelector('[data-ih-brain-current]');
    const button = root.querySelector('[data-ih-brain-motion-toggle]');
    const label = root.querySelector('[data-ih-brain-motion-label]');
    if (!steps.length || !button || !label) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let paused = reducedMotion.matches;
    let visible = false;
    let queued = false;
    let destroyed = false;
    const setMotion = () => {
      root.dataset.ihMotion = paused ? 'paused' : visible ? 'active' : 'static';
      button.hidden = false;
      button.setAttribute('aria-pressed', String(paused));
      label.textContent = paused ? label.dataset.resume : label.dataset.pause;
      button.lastElementChild.textContent = paused ? '▷' : 'Ⅱ';
    };
    const draw = () => {
      queued = false;
      if (destroyed || !visible) return;
      const viewportHeight = window.innerHeight;
      const mobile = window.matchMedia('(max-width: 760px)').matches;
      const centered = root.hasAttribute('data-ih-brain-centered');
      const target = viewportHeight * (centered ? .5 : mobile ? .77 : .5);
      const boxes = steps.map((step) => step.getBoundingClientRect());
      let active = 0;
      let shortestDistance = Infinity;
      boxes.forEach((box, index) => {
        const distance = target < box.top ? box.top - target : target > box.bottom ? target - box.bottom : 0;
        if (distance < shortestDistance) {
          shortestDistance = distance;
          active = index;
        }
      });
      root.dataset.ihBrainStepState = String(active + 1);
      let stepProgress = clamp((target - boxes[active].top) / Math.max(1, boxes[active].height));
      if (centered) {
        const reveal = steps[active].querySelector('[data-ih-brain-reveal]');
        if (reveal) {
          const revealBox = reveal.getBoundingClientRect();
          stepProgress = clamp((target - revealBox.top) / Math.max(1, revealBox.height));
        } else if (active === 2) stepProgress = 1;
      }
      root.style.setProperty('--ih-brain-step-progress', stepProgress.toFixed(4));
      const bandCards = Array.from(root.querySelectorAll('[data-ih-brain-band]'));
      if (active === 2 && bandCards.length) {
        let closest = bandCards[0];
        let distance = Infinity;
        bandCards.forEach((card) => {
          const box = card.getBoundingClientRect();
          const nextDistance = target >= box.top && target <= box.bottom ? -1 : Math.abs((box.top + box.bottom) / 2 - target);
          if (nextDistance < distance) { closest = card; distance = nextDistance; }
        });
        root.dataset.ihActiveBand = closest.dataset.ihBrainBand;
      } else delete root.dataset.ihActiveBand;

      if (counter) counter.textContent = String(active + 1).padStart(2, '0');
      const total = Math.max(1, boxes[boxes.length - 1].bottom - boxes[0].top - viewportHeight * .35);
      const progress = clamp((target - boxes[0].top) / total);
      root.style.setProperty('--ih-brain-progress', progress.toFixed(4));
      let front = .16;
      let parietal = .16;
      let temporal = .16;
      let occipital = .16;
      let region = 'all';
      if (active === 3) {
        front = .9;
        region = 'front';
      } else if (active === 4) {
        parietal = .82;
        temporal = .82;
        region = 'sides';
      } else if (active === 5) {
        occipital = .9;
        region = 'back';
      } else if (active > 5) {
        front = .35;
        parietal = .35;
        temporal = .35;
        occipital = .35;
      }
      root.dataset.ihBrainRegionState = region;
      root.style.setProperty('--ih-region-front', String(front));
      root.style.setProperty('--ih-region-parietal', String(parietal));
      root.style.setProperty('--ih-region-temporal', String(temporal));
      root.style.setProperty('--ih-region-occipital', String(occipital));
      root.style.setProperty('--ih-wave-opacity', active === 2 ? '.95' : active < 2 ? '.35' : '.6');

    };
    const schedule = () => {
      if (queued || destroyed || !visible) return;
      queued = true;
      window.requestAnimationFrame(draw);
    };
    const onScroll = () => schedule();
    const onResize = () => schedule();
    button.addEventListener('click', () => {
      paused = !paused;
      setMotion();
      schedule();
    });
    const onMotionChange = () => {
      paused = reducedMotion.matches;
      setMotion();
      schedule();
    };
    reducedMotion.addEventListener('change', onMotionChange);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    let observer;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        const entry = entries[0];
        visible = entry.isIntersecting;
        setMotion();
        if (visible) schedule();
      }, { rootMargin: '80px 0px', threshold: 0 });
      observer.observe(root);
    } else {
      visible = true;
    }
    setMotion();
    schedule();
    root.addEventListener('ih:brain-scroll:destroy', () => {
      destroyed = true;
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      reducedMotion.removeEventListener('change', onMotionChange);
      if (observer) observer.disconnect();
    }, { once: true });
  };
  const initialiseWithin = (container) => {
    if (container.matches && container.matches('[data-ih-brain-scroll]')) initialise(container);
    container.querySelectorAll('[data-ih-brain-scroll]').forEach(initialise);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialiseWithin(document), { once: true });
  else initialiseWithin(document);
  document.addEventListener('shopify:section:load', (event) => initialiseWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-ih-brain-scroll]').forEach((root) => root.dispatchEvent(new Event('ih:brain-scroll:destroy')));
  });
})();
